'use client';

import { Input } from '@headlessui/react';
import { type FocusEvent, type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { IconCalendar } from '@/components/icons';
import { cn } from '@/lib/utils/tw-merge';
import { DATE_PICKER_LABELS, DATE_PICKER_PLACEHOLDER } from './UiDatePicker.constants';
import type { UiDatePickerProps } from './UiDatePicker.types';
import { type DateRange, draftMessage, formatDay, isInRange, normalizeIsoDay, parseDay } from './UiDatePicker.utils';
import { UiDatePickerPopover } from './UiDatePickerPopover';
import { useCoarsePointer } from './useCoarsePointer';
import { useFormReset } from './useFormReset';
import { useOutsidePointerDown } from './useOutsidePointerDown';

/** How the calendar was opened decides whether the focus moves into it. */
type OpenState = false | 'pointer' | 'keyboard';

/** Shows a touch device's own picker for the native date input. */
function showNativePicker(input: HTMLInputElement | null) {
    try {
        input?.showPicker();
    } catch {
        input?.focus();
    }
}

/**
 * Date field with the design's calendar: typed as TT.MM.JJJJ or picked in the
 * popover. On touch devices the native date input and its picker take over.
 * Either way the form submits the ISO day under `name`, as a native date input
 * does, and validation stays the browser's (`required`, a custom message for
 * a typed day that does not exist or lies out of range).
 */
export function UiDatePicker({
    name,
    label,
    defaultValue,
    min,
    max,
    required,
    title,
    placeholder = DATE_PICKER_PLACEHOLDER,
    className,
}: UiDatePickerProps) {
    const touch = useCoarsePointer();
    const range = useMemo<DateRange>(
        () => ({ min: normalizeIsoDay(min) || undefined, max: normalizeIsoDay(max) || undefined }),
        [min, max],
    );
    const initialValue = normalizeIsoDay(defaultValue);
    const [value, setValue] = useState(initialValue);
    const [draft, setDraft] = useState<string | null>(null);
    const [open, setOpen] = useState<OpenState>(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const popoverId = useId();
    const message = touch ? '' : draftMessage(draft, range);

    useEffect(() => {
        inputRef.current?.setCustomValidity(message);
    }, [message, touch]);

    useFormReset(inputRef, () => {
        setValue(initialValue);
        setDraft(null);
        setOpen(false);
    });

    useOutsidePointerDown(Boolean(open), rootRef, () => setOpen(false));

    const close = () => {
        setOpen(false);
        inputRef.current?.focus();
    };

    const select = (iso: string) => {
        setValue(iso);
        setDraft(null);
        close();
    };

    // A typed day in range becomes the value; anything else submits nothing and
    // keeps its text, so the message above blocks the submit.
    const commit = () => {
        if (draft === null) return;

        const text = draft.trim();
        const iso = parseDay(text);

        if (!text) {
            setValue('');
            setDraft(null);
        } else if (iso && isInRange(iso, range)) {
            setValue(iso);
            setDraft(null);
        } else {
            setValue('');
        }
    };

    const openFromKeyboard = () => {
        if (open) {
            document.getElementById(popoverId)?.querySelector<HTMLElement>('[data-focused]')?.focus();
            return;
        }

        commit();
        setOpen('keyboard');
    };

    const toggle = () => {
        if (touch) return showNativePicker(inputRef.current);
        if (open) return close();

        openFromKeyboard();
    };

    const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            commit();
        } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            openFromKeyboard();
        } else if (event.key === 'Escape' && open) {
            // Prevented, so a dialog around the field stays open.
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
        }
    };

    // Tabbing out of the field and its calendar closes the calendar.
    const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
        const next = event.relatedTarget as Node | null;
        if (open && next && !rootRef.current?.contains(next)) setOpen(false);
    };

    return (
        <div ref={rootRef} className={cn('form-date', className)} onBlur={handleBlur}>
            {touch ? (
                <Input
                    ref={inputRef}
                    type="date"
                    className="form-input"
                    required={required}
                    title={title}
                    min={range.min}
                    max={range.max}
                    value={value}
                    onChange={(event) => setValue(event.target.value)}
                />
            ) : (
                <Input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className="form-input"
                    placeholder={placeholder}
                    required={required}
                    title={title}
                    value={draft ?? formatDay(value)}
                    aria-haspopup="dialog"
                    aria-expanded={Boolean(open)}
                    aria-controls={open ? popoverId : undefined}
                    onChange={(event) => setDraft(event.target.value)}
                    onBlur={commit}
                    onClick={() => {
                        if (!open) setOpen('pointer');
                    }}
                    onKeyDown={handleInputKeyDown}
                />
            )}

            <button
                type="button"
                className="form-date-toggle"
                aria-label={open ? DATE_PICKER_LABELS.close : DATE_PICKER_LABELS.open}
                aria-haspopup="dialog"
                aria-expanded={Boolean(open)}
                aria-controls={open ? popoverId : undefined}
                onClick={toggle}
            >
                <IconCalendar className="size-4" aria-hidden="true" />
            </button>

            {name ? <input type="hidden" name={name} value={value} /> : null}

            {open ? (
                <UiDatePickerPopover
                    // A newly committed day re-centres the open calendar on it.
                    key={value}
                    id={popoverId}
                    label={label || DATE_PICKER_LABELS.dialog}
                    value={value}
                    range={range}
                    anchorRef={rootRef}
                    autoFocus={open === 'keyboard'}
                    onSelect={select}
                    onClear={() => select('')}
                    onClose={close}
                />
            ) : null}
        </div>
    );
}
